/**
 * Shared utilities for automated flight & quote status transitions.
 *
 * Quote status rules (from spec):
 *   Any flight DIVERTED    → Quote DIVERTED
 *   Any flight IN_TRANSIT  → Quote IN_TRANSIT
 *   ALL flights COMPLETED  → Quote COMPLETED
 *   ALL flights CANCELLED  → Quote CANCELLED
 */
import { apiClient } from '@/lib/api';

/**
 * Fetch all flights on a quote, apply `newFlightStatus` for `updatedFlightId`
 * in memory, then return the appropriate new quote status (or null if no change).
 */
export async function deriveQuoteStatus(quoteId, updatedFlightId, newFlightStatus) {
  try {
    const data = await apiClient.getFlights({ quote_id: quoteId });
    const allFlights = Array.isArray(data) ? data : [];
    if (!allFlights.length) return null;

    const statuses = allFlights.map((f) =>
      (f.flight_id || f.id) === updatedFlightId
        ? newFlightStatus
        : (f.flight_status || 'POSITIONING').toUpperCase()
    );

    if (statuses.some((s) => s === 'DIVERTED')) return 'DIVERTED';
    if (statuses.some((s) => s === 'IN_TRANSIT')) return 'IN_TRANSIT';
    if (statuses.every((s) => s === 'COMPLETED')) return 'COMPLETED';
    if (statuses.every((s) => s === 'CANCELLED')) return 'CANCELLED';

    return null;
  } catch (err) {
    console.error('[flightStatusUtils] deriveQuoteStatus failed:', err);
    return null;
  }
}

/**
 * Ask FR24 whether a past-due, still-pre-flight flight actually departed,
 * so callers can avoid a false DELAYED label. Returns the status the flight
 * should take based on FR24 evidence, or `null` when FR24 is silent — in which
 * case the caller should fall back to its time-based DELAYED rule.
 *
 *   'COMPLETED' / 'DIVERTED'  an ended summary matched the booked route
 *   'IN_TRANSIT'              a matching summary exists but hasn't ended yet
 *   null                      no registration / no matching record / fetch error
 *
 * Route validation (booked origin AND destination must match) prevents picking
 * up a different flight the same registration flew that day.
 */
export async function resolveDepartedFromSummary(flight) {
  const tail = flight?.aircraft?.tail_number;
  const ref = flight?.departure_date || flight?.arrival_date;
  if (!tail || !ref) return null;

  const refMs = new Date(ref).getTime();
  if (Number.isNaN(refMs)) return null;
  const from = new Date(refMs - 2 * 60 * 60 * 1000).toISOString();
  const to = new Date(refMs + 24 * 60 * 60 * 1000).toISOString();

  let summaries = [];
  try {
    const data = await apiClient.getFlightSummary(tail, from, to);
    summaries = Array.isArray(data) ? data : (data?.data || []);
  } catch {
    return null; // FR24 unavailable → treat as silent
  }
  if (!summaries.length) return null;

  const expectedDep = (flight.departure_airport || '').toUpperCase();
  const expectedArr = (flight.arrival_airport || '').toUpperCase();
  const match = summaries.find((s) => {
    const sDep = (s.orig_icao || s.orig_iata || '').toUpperCase();
    const sArr = (s.dest_icao || s.dest_iata || '').toUpperCase();
    return sDep === expectedDep && sArr === expectedArr;
  });

  if (!match) return null;            // no record for our route → silent
  if (!match.flight_ended) return 'IN_TRANSIT'; // still airborne on our route

  const sActual = (match.dest_icao_actual || '').toUpperCase();
  return sActual && sActual !== expectedArr ? 'DIVERTED' : 'COMPLETED';
}
